import type { UpdateSystem } from "@engine/core/Lifecycle";
import type { EcsWorld } from "../world";

const MOVE_THRESHOLD_SQ = 0.5 * 0.5;

/**
 * Drives `AnimationStateMachine` for every entity with `animated`. State is
 * derived from velocity:
 *
 *   - moving (>0.5 px/s) → "walk"
 *   - stopped            → "idle"
 *
 * If the entity already has an explicit `animated.state` not in those two
 * (e.g. "attack", "hurt"), it is preserved (the gameplay code that triggered
 * it owns the transition back).
 *
 * Heading is taken from `entity.heading` (radians).
 */
export class AnimationSystem implements UpdateSystem {
  private query;

  constructor(world: EcsWorld) {
    this.query = world.with("animated");
  }

  update(dt: number): void {
    for (const e of this.query) {
      const a = e.animated;
      if (!a) continue;

      // Auto-transition between idle/walk if the gameplay layer hasn't claimed
      // a different state. For non-locomotion states (attack, hurt, …) the
      // state machine owns transitions (e.g. attack → idle when its clip
      // finishes), so we sync `a.state` from the SM after that.
      if (a.state === "idle" || a.state === "walk") {
        const v = e.velocity;
        const movingSq = v ? v.x * v.x + v.y * v.y : 0;
        a.state = movingSq > MOVE_THRESHOLD_SQ ? "walk" : "idle";
      }

      if (typeof e.heading === "number") a.sm.setHeading(e.heading);
      a.sm.transition(a.state);
      a.sm.update(dt, a.sprite);
      // Pull the SM's current state back so any auto-transitions done by
      // `next` reach the entity (e.g. attack → idle).
      a.state = a.sm.current();
    }
  }
}
